import { InvoiceAggregateGroupBy, type InvoiceAggregateSeriesBy } from './invoice-aggregate';
import { ApprovalStatus } from './approval-status';

/** Translation sets whose keys a chat chart's x-values or series names may be. */
export const ChartLabelSet = {
  INVOICE_STATUS: 'invoiceStatus',
  WEEKDAY: 'weekday',
  PAYMENT_METHOD: 'paymentMethod'
} as const;
export type ChartLabelSet = (typeof ChartLabelSet)[keyof typeof ChartLabelSet];

/** Chart texts the backend sends as keys, never as words — the web-app translates them. */
export const ChartTextKey = {
  UNKNOWN: '__unknown__',
  UNKNOWN_SUPPLIER: '__unknownSupplier__',
  UNKNOWN_PERIOD: '__unknownPeriod__',
  INVOICES: '__invoices__'
} as const;
export type ChartTextKey = (typeof ChartTextKey)[keyof typeof ChartTextKey];

const CHART_TEXT_KEYS: readonly string[] = Object.values(ChartTextKey);

/** Whether a chart label is a text key rather than display text. */
export const isChartTextKey = (value: string): value is ChartTextKey =>
  CHART_TEXT_KEYS.includes(value);

/** How the web-app formats a chat chart's series names. */
export const ChartSeriesFormat = {
  DATE_RANGE: 'dateRange'
} as const;
export type ChartSeriesFormat = (typeof ChartSeriesFormat)[keyof typeof ChartSeriesFormat];

/** Separator between the two ISO dates of a `dateRange` series name. */
export const CHART_DATE_RANGE_SEPARATOR = ' – ';

/** ISO weekdays, Monday first — the keys of the `weekday` label set. */
export const CHART_WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
] as const;

/** Invoice approval status → key of the `invoiceStatus` label set. */
export const INVOICE_APPROVAL_STATUS_CHART_KEYS: Record<ApprovalStatus, string> = {
  [ApprovalStatus.UNPROCESSED]: 'draft',
  [ApprovalStatus.PENDING]: 'forApproval',
  [ApprovalStatus.FOR_REVIEW]: 'forApproval',
  [ApprovalStatus.APPROVED]: 'approved',
  [ApprovalStatus.REJECTED]: 'rejected'
};

/** Label set translating the values of an aggregate dimension; none when they are display text. */
export function chartLabelSetOf(
  dimension: InvoiceAggregateGroupBy | InvoiceAggregateSeriesBy
): ChartLabelSet | undefined {
  switch (dimension) {
    case InvoiceAggregateGroupBy.WEEKDAY:
      return ChartLabelSet.WEEKDAY;
    case InvoiceAggregateGroupBy.STATUS:
      return ChartLabelSet.INVOICE_STATUS;
    case InvoiceAggregateGroupBy.PAYMENT_METHOD:
      return ChartLabelSet.PAYMENT_METHOD;
    default:
      return undefined;
  }
}
